import { useState } from "react";
import ReactMarkdown from "react-markdown";

// Simple text icons as fallback
const TwitterIcon = () => <span className="text-blue-400">𝕏</span>;
const LinkedinIcon = () => <span className="text-blue-600">in</span>;
const RedditIcon = () => <span className="text-orange-500">R/</span>;

interface ResultsProps {
  onBack: () => void;
  content: {
    twitter?: string;
    linkedin?: string;
    reddit?: string;
    [key: string]: string | undefined;
  } | null;
}

type Platform = 'twitter' | 'linkedin' | 'reddit';

const platformConfig = {
  twitter: {
    name: "Twitter",
    icon: <TwitterIcon />,
    color: "bg-purple-50 border-purple-100",
    textColor: "text-purple-700",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    button: "bg-purple-50 hover:bg-purple-100 text-purple-600 border-purple-200"
  },
  linkedin: {
    name: "LinkedIn",
    icon: <LinkedinIcon />,
    color: "bg-purple-50 border-purple-100",
    textColor: "text-purple-700",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    button: "bg-purple-50 hover:bg-purple-100 text-purple-600 border-purple-200"
  },
  reddit: {
    name: "Reddit",
    icon: <RedditIcon />,
    color: "bg-purple-50 border-purple-100",
    textColor: "text-purple-700",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    button: "bg-purple-50 hover:bg-purple-100 text-purple-600 border-purple-200"
  }
} as const;

const Results: React.FC<ResultsProps> = ({ onBack, content }) => {
  const [copied, setCopied] = useState<Platform | null>(null);

  const copyToClipboard = async (text: string, platform: Platform) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(platform);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadAsTxt = (text: string, platform: string) => {
    const element = document.createElement('a');
    const file = new Blob([text], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `reverb-${platform}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-card">
      <div className="form-header">
        <div className="back-button-container">
          <button 
            type="button" 
            className="back-button"
            onClick={onBack}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor" 
              className="back-button-icon"
            >
              <path 
                fillRule="evenodd" 
                d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" 
                clipRule="evenodd" 
              />
            </svg>
            Back
          </button>
        </div>
        <h2 className="form-title">
          Your Repurposed Content
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="title-icon">
            <path fillRule="evenodd" d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5Z" clipRule="evenodd" />
            <path d="M18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z" />
          </svg>
        </h2>
        <p className="form-subtitle">Here are your platform-optimized posts</p>
      </div>

      <div className="results-container">
        {(Object.keys(platformConfig) as Platform[]).map((platform) => (
          <div 
            key={platform} 
            className={`result-card ${platformConfig[platform].borderColor}`}
          >
            <div className={`result-header ${platformConfig[platform].bgColor}`}>
              <span className="platform-icon">{platformConfig[platform].icon}</span>
              <h3 className="platform-name">{platformConfig[platform].name} Post</h3>
            </div>
            
            <div className="result-content">
              <div className="content-text">
                {content[platform] ? (
                  <div className="markdown-content">
                    <ReactMarkdown>
                      {content[platform] || ""}
                    </ReactMarkdown>
                  </div>
                ) : (
                  "No content generated for this platform."
                )}
              </div>
              
              <div className="result-actions">
                <button
                  onClick={() => copyToClipboard(content[platform] || '', platform)}
                  className={`action-button ${platformConfig[platform].button}`}
                >
                  {copied === platform ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="button-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="button-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => downloadAsTxt(content[platform] || '', platform)}
                  className="action-button secondary-action"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="button-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="form-footer">
        <button
          onClick={onBack}
          className="cta-button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="button-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Generate Another
        </button>
      </div>
    </div>
  );
};

export default Results;